import { useState } from 'react';
import { Button } from '@leadsnavi/ui-kit';
import validator from 'validator';
import cn from 'classnames';
import type { LoginStepProps } from '../types';
import { GoogleAuthButton, OrDivider, FormField, PasswordInput, PrivacyCheckbox } from '../shared';

/**
 * 登录步骤页面
 */
export function LoginStep({
  errors: externalErrors,
  loading = false,
  googleAuthUrl,
  onStepChange,
  onPrivacyPolicyClick,
  onSubmit,
  onForgotPasswordClick,
  initialEmail = '',
}: LoginStepProps) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const errors = { ...localErrors, ...externalErrors };

  /**
   * 验证表单
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validator.isEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 提交表单
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit?.(email, password, agreedToPolicy);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-[32px]">
      {/* 标题 */}
      <div className="flex flex-col gap-[12px] items-start">
        <h2 className="text-[30px] font-semibold leading-none tracking-[-0.3px] text-[#09090b]">Log in</h2>
        <div className="flex items-center gap-[8px] text-[14px] leading-[1.5] tracking-[0.07px]">
          <span className="text-[#525252]">Don't have an account yet?</span>
          <button
            type="button"
            onClick={() => onStepChange?.('signup')}
            className="text-[#10b981] underline hover:text-[#059669]"
          >
            Sign up
          </button>
        </div>
      </div>

      {/* 表单内容 */}
      <div className="flex flex-col gap-[24px]">
        {/* Google 登录 + OR 分隔线 */}
        <div className="flex flex-col gap-[24px]">
          <GoogleAuthButton text="Log in with Google" href={googleAuthUrl} />
          <OrDivider />
        </div>

        {/* 邮箱和密码输入 */}
        <div className="flex flex-col gap-[24px]">
          <div className="flex flex-col gap-[28px]">
            <FormField
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              error={errors.email}
              disabled={loading}
            />

            <PasswordInput
              label="Password"
              value={password}
              onChange={setPassword}
              error={errors.password}
              disabled={loading}
              rightLabel={
                <button
                  type="button"
                  onClick={onForgotPasswordClick}
                  className="text-[14px] font-normal leading-[1.5] tracking-[0.07px] text-[#737373] hover:text-[#525252]"
                >
                  Forgot your password?
                </button>
              }
            />
          </div>

          {/* Next 按钮 + 隐私政策 */}
          <div className="flex flex-col items-center gap-[20px]">
            <Button
              type="submit"
              disabled={loading || !agreedToPolicy}
              loading={loading}
              className={cn(
                'h-[40px] w-full rounded-[8px] bg-[#171717] px-[24px] py-[9.5px]',
                'text-[14px] font-medium leading-[1.5] tracking-[0.07px] text-white',
                'hover:bg-[#262626]',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              Next
            </Button>

            <PrivacyCheckbox
              checked={agreedToPolicy}
              onChange={setAgreedToPolicy}
              onPrivacyPolicyClick={onPrivacyPolicyClick}
            />
          </div>
        </div>
      </div>
    </form>
  );
}

export default LoginStep;
